import type { BootGroup, BootState, ViewEndpoint } from "../protocol/events";
import { make } from "./dom";
import "./boot.css";

export interface BootScreen {
  /** Lift the scrim when scanning starts, or remove it immediately for camera-off. */
  hide(animate?: boolean): void;
  dispose(): void;
}

const CAMERA_ALLOWED_KEY = "webscan.camera-allowed";
const emptyTasks: BootGroup[] = [
  {
    id: "detector",
    label: "Card detector",
    optional: false,
    status: "queued",
    loaded: 0,
    total: 0,
  },
  {
    id: "recogniser",
    label: "Card recogniser",
    optional: false,
    status: "queued",
    loaded: 0,
    total: 0,
  },
  {
    id: "index",
    label: "Card index & prices",
    optional: false,
    status: "queued",
    loaded: 0,
    total: 0,
  },
  { id: "extras", label: "Extras", optional: true, status: "queued", loaded: 0, total: 0 },
];

/** Four byte-weighted tasks, with camera permission independent of asset readiness. */
export function mountBoot(host: HTMLElement, endpoint: ViewEndpoint): BootScreen {
  host.className = "boot-screen";
  host.hidden = false;
  host.setAttribute("aria-label", "Preparing scanner");
  const brand = make("div", "boot-screen__brand", "webscan");
  const centre = make("div", "boot-screen__centre");
  const number = make("div", "boot-screen__number");
  const percentage = make("span", "boot-screen__percentage", "0");
  number.append(percentage, make("span", "boot-screen__percent-sign", "%"));
  const rail = make("div", "boot-screen__rail");
  rail.setAttribute("role", "progressbar");
  rail.setAttribute("aria-label", "Scanner download");
  rail.setAttribute("aria-valuemin", "0");
  rail.setAttribute("aria-valuemax", "100");
  const fill = make("div", "boot-screen__fill");
  rail.append(fill);
  const meta = make("div", "boot-screen__meta");
  const bytes = make("span", "", "0.0 / 0.0 MB");
  const current = make("span", "", "preparing");
  meta.append(bytes, current);
  const context = make("p", "boot-screen__context");
  centre.append(number, rail, meta, context);
  const tasks = make("div", "boot-screen__tasks");
  tasks.setAttribute("role", "list");
  tasks.setAttribute("aria-label", "Scanner preparation tasks");
  const rows = new Map<string, { row: HTMLElement; glyph: HTMLElement; status: HTMLElement }>();
  for (const task of emptyTasks) {
    const row = make("div", "boot-screen__task");
    row.setAttribute("role", "listitem");
    const glyph = make("span", "boot-screen__glyph");
    glyph.setAttribute("aria-hidden", "true");
    const label = make("span", "boot-screen__task-label", task.label);
    if (task.optional) label.append(make("span", "boot-screen__optional", "optional"));
    const status = make("span", "boot-screen__task-status");
    row.append(glyph, label, status);
    tasks.append(row);
    rows.set(task.id, { row, glyph, status });
  }
  const actions = make("div", "boot-screen__actions");
  const allow = make("button", "boot-screen__primary", "Allow camera");
  allow.id = "allowCameraBtn";
  allow.type = "button";
  const retry = make("button", "boot-screen__primary", "Retry");
  retry.id = "bootRetry";
  retry.type = "button";
  const copy = make("div", "boot-screen__copy");
  copy.setAttribute("role", "status");
  const detailToggle = make("button", "boot-screen__detail-toggle", "show technical detail");
  detailToggle.type = "button";
  detailToggle.setAttribute("aria-expanded", "false");
  detailToggle.setAttribute("aria-controls", "bootTechnicalDetail");
  const detail = make("pre", "boot-screen__detail");
  detail.id = "bootTechnicalDetail";
  detail.hidden = true;
  actions.append(allow, retry, copy, detailToggle, detail);
  host.replaceChildren(brand, centre, tasks, actions);

  let state: BootState = { loaded: 0, total: 0, fromCache: false, tasks: emptyTasks };
  let failed = false;
  let ready = false;
  let requested = false;
  let live = false;
  let blocked = false;
  let permitted = false;
  let disposed = false;
  let hiding = false;
  /** The device took the preview's camera away before scanning began. */
  let lost = false;
  let heldPercentage = 0;
  let heldBytes = "0.0 / 0.0 MB";
  let hideTimer = 0;
  let permissionStatus: PermissionStatus | null = null;
  const technical = new Map<number, string>();
  const labels = new Map<number, string>();

  const render = (): void => {
    if (disposed) return;
    host.dataset.state = failed ? "failed" : live ? "returning" : "loading";
    if (!failed) {
      heldPercentage = ready
        ? 100
        : Math.min(99, state.total ? Math.floor((state.loaded / state.total) * 100) : 0);
      heldBytes = `${(state.loaded / 1e6).toFixed(1)} / ${(state.total / 1e6).toFixed(1)} MB`;
    }
    percentage.textContent = String(heldPercentage);
    fill.style.width = `${heldPercentage}%`;
    rail.setAttribute("aria-valuenow", String(heldPercentage));
    bytes.textContent = heldBytes;
    const active = state.tasks.find((task) => !task.optional && task.status === "loading");
    current.textContent = failed
      ? "stopped"
      : state.fromCache
        ? "from cache"
        : ready
          ? "ready"
          : (active?.label.toLowerCase() ?? "preparing");
    context.textContent = failed
      ? "The download was interrupted. A retry resumes from what already arrived."
      : live
        ? state.fromCache
          ? "Served from cache. Only what changed since the last session is fetched."
          : "Only what changed since the last session is fetched. The models stay cached."
        : "First run only — the models stay cached for every session after this one.";
    for (const task of state.tasks) {
      const entry = rows.get(task.id);
      if (!entry) continue;
      entry.row.dataset.state = task.status;
      entry.glyph.textContent = { ready: "✓", loading: "↓", queued: "·", failed: "!" }[task.status];
      entry.status.textContent =
        task.status === "failed"
          ? task.optional
            ? "skipped"
            : "failed"
          : task.status === "ready"
            ? "ready"
            : task.optional
              ? "background"
              : task.status === "queued"
                ? "queued"
                : `${task.total ? Math.min(99, Math.floor((task.loaded / task.total) * 100)) : 0}%`;
    }
    allow.hidden = failed || (permitted && !lost) || live || blocked;
    allow.disabled = requested;
    allow.setAttribute("aria-busy", String(requested));
    retry.hidden = !failed;
    detailToggle.hidden = !failed;
    if (!failed) detail.hidden = true;
    copy.hidden = failed;
    const extrasFailed = state.tasks.some((task) => task.optional && task.status === "failed");
    copy.textContent = blocked
      ? "Camera access is blocked. The scanner download will finish first."
      : lost
        ? "Your device closed the camera. Allow it again to continue."
        : extrasFailed
          ? "Some extras were skipped. Scanning opens with reduced accuracy."
          : live || permitted
            ? "Camera already allowed — this opens into scanning by itself."
            : "Answer now and scanning opens by itself when the download lands. Recognition runs on your device.";
  };
  const requestCamera = (): void => {
    if (disposed || host.hidden || hiding || requested || live || document.hidden) return;
    requested = true;
    blocked = false;
    lost = false;
    render();
    endpoint.emit("camera:prepare");
  };
  allow.onclick = requestCamera;
  retry.onclick = () => {
    retry.disabled = true;
    retry.setAttribute("aria-busy", "true");
    endpoint.emit("boot:retry");
  };
  detailToggle.onclick = () => {
    detail.hidden = !detail.hidden;
    detailToggle.textContent = detail.hidden ? "show technical detail" : "hide technical detail";
    detailToggle.setAttribute("aria-expanded", String(!detail.hidden));
  };
  const subscriptions = [
    endpoint.on("boot:task", ({ id, label }) => {
      labels.set(id, label);
    }),
    endpoint.on("boot:state", (next) => {
      state = next;
      if (!failed && next.tasks.some((task) => !task.optional && task.status === "failed")) {
        failed = true;
      }
      render();
    }),
    endpoint.on("boot:failed", ({ id, message }) => {
      technical.set(id, `${labels.get(id) ?? "Asset"}: ${message}`);
      detail.textContent = [...technical.values()].join("\n\n");
    }),
    endpoint.on("boot:error", ({ message }) => {
      failed = true;
      detail.textContent = `${message}\n\n${[...technical.values()].join("\n\n")}`;
      retry.disabled = false;
      retry.removeAttribute("aria-busy");
      render();
    }),
    endpoint.on("boot:retrying", () => {
      failed = false;
      technical.clear();
      retry.disabled = false;
      retry.removeAttribute("aria-busy");
      detailToggle.setAttribute("aria-expanded", "false");
      detailToggle.textContent = "show technical detail";
      render();
    }),
    endpoint.on("boot:ready", () => {
      ready = true;
      render();
    }),
    endpoint.on("camera:opened", () => {
      live = true;
      requested = false;
      permitted = true;
      blocked = false;
      lost = false;
      try {
        localStorage.setItem(CAMERA_ALLOWED_KEY, "true");
      } catch {
        /* Storage is optional. */
      }
      render();
    }),
    endpoint.on("camera:failed", () => {
      requested = false;
      live = false;
      permitted = false;
      blocked = true;
      render();
    }),
    endpoint.on("session:stopped", ({ reason }) => {
      live = false;
      requested = false;
      // Whatever the permission state says, the button is the way to ask for it again.
      lost = reason === "camera-lost";
      render();
    }),
  ];
  const permissionChanged = (): void => {
    if (permissionStatus?.state === "granted") {
      permitted = true;
      requestCamera();
    } else if (permissionStatus?.state === "denied") {
      permitted = false;
      blocked = true;
      render();
    }
  };
  const visibilityChanged = (): void => {
    if (!document.hidden && permitted && !failed) requestCamera();
  };
  document.addEventListener("visibilitychange", visibilityChanged);
  // Mount precedes scanner construction. Defer the advisory query so its event cannot
  // race the scanner listener; an unsupported Permissions API still keeps the button.
  queueMicrotask(
    () =>
      void (async () => {
        try {
          if (!navigator.permissions?.query) throw new Error("Permissions query unavailable");
          permissionStatus = await navigator.permissions.query({
            name: "camera" as PermissionName,
          });
          if (disposed) return;
          permissionStatus.addEventListener("change", permissionChanged);
          permissionChanged();
        } catch {
          try {
            if (localStorage.getItem(CAMERA_ALLOWED_KEY) === "true") requestCamera();
          } catch {
            /* Storage is optional. */
          }
        }
      })(),
  );
  render();
  return {
    hide(animate = true) {
      if (host.hidden) return;
      hiding = true;
      host.classList.add("boot-screen--leaving");
      host.inert = true;
      if (animate && !matchMedia("(prefers-reduced-motion: reduce)").matches) {
        hideTimer = window.setTimeout(() => {
          host.hidden = true;
        }, 240);
      } else host.hidden = true;
    },
    dispose() {
      disposed = true;
      clearTimeout(hideTimer);
      permissionStatus?.removeEventListener("change", permissionChanged);
      document.removeEventListener("visibilitychange", visibilityChanged);
      subscriptions.forEach((unsubscribe) => unsubscribe());
    },
  };
}
